
import Vue from 'vue';
import { mapState } from 'vuex';
import _debounce from 'lodash/debounce';
import GlassButton from '@/components/base/GlassButton.vue';
import { LIBRARY_SEARCH_MIN_LENGTH } from '@/utils/libraryApi';

const DEBOUNCE_MS = 300;

// The Library Search box: a field that holds what the Caller types and emits
// `input` with the committed term: trimmed, '' below the minimum length, and
// only when it differs from `value`. On wide screens the field always shows. On
// small screens it starts as a search button; the field stays open while a term
// is applied, and turns back into the button once it is empty and loses focus.
export default Vue.extend({
	name: 'LibrarySearchField',
	props: {
		value: { type: String, default: '' },
		// Passed to the closed button, so a host can match its own buttons.
		buttonStyles: { type: Object, default: () => ({}) },
	},
	data () {
		return {
			text: '' as string | null,
			opened: false,
			focused: false,
			debouncedCommit: null as ReturnType<typeof _debounce> | null,
		};
	},
	computed: {
		...mapState(['user']),
		expanded (): boolean {
			return !this.$vuetify.breakpoint.smAndDown || this.opened || !!this.value;
		},
	},
	watch: {
		// A host that resets the term (a dialog tab change, a URL seed, the empty
		// state's Clear search) moves the box to match. Text that already commits
		// to the same term stays as typed. Cleared while nobody is typing, it closes.
		value: {
			immediate: true,
			handler (value: string) {
				if (this.termFor(this.text) !== value) this.text = value;
				if (!value && !this.focused) this.opened = false;
			},
		},
	},
	created () {
		this.debouncedCommit = _debounce(this.commit, DEBOUNCE_MS);
	},
	beforeDestroy () {
		if (this.debouncedCommit) this.debouncedCommit.cancel();
	},
	methods: {
		termFor (text: string | null): string {
			const trimmed: string = (text || '').trim();
			return trimmed.length >= LIBRARY_SEARCH_MIN_LENGTH ? trimmed : '';
		},
		commit () {
			if (this.debouncedCommit) this.debouncedCommit.cancel();
			const term: string = this.termFor(this.text);
			if (term !== this.value) this.$emit('input', term);
		},
		open () {
			this.opened = true;
			this.$nextTick(() => {
				(this.$refs.field as Vue & { focus: () => void }).focus();
			});
		},
		onClear () {
			this.text = '';
			this.commit();
		},
		onBlur () {
			this.focused = false;
			if (!(this.text || '').trim()) this.opened = false;
		},
	},
	components: {
		GlassButton,
	},
});
